import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { withRouter } from "react-router-dom";
import Select from "react-select";
import {
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
  UncontrolledDropdown
} from "reactstrap";
import Draggable from "react-draggable";
import swal from "sweetalert";
import {
  convertDataFromSnakeCaseToCamelCase,
  convertToDateString,
  LoadingSpinner,
} from "../../../constants/const";

import moment from "moment";
import { BiSolidEdit } from "react-icons/bi";
import { BsTrash3Fill } from "react-icons/bs";
import { FiDownload } from "react-icons/fi";
import { IoArrowBackSharp } from "react-icons/io5";
import { PiEyeDuotone } from "react-icons/pi";
import { toast, ToastContainer } from "react-toastify";
import { encryptStorage } from "../../../constants/EncryptStorage";
import { GetLanguageString, NoResult, BouncingLoader } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import DateFilterUI from "../../helper/SearchFilter/DateFilterUI";
import { ShowToast } from "../../shared/ShowToast";
import { ApiFinance } from "../ApiFinance";
import EditCashToAmanatTransaction from "./cashInHandToAmanat/EditCashToAmanatTransaction";
import EditMarkazShare from "./EditMarkazShare";

const ManageTransfer = ({ history }) => {
  const { t } = useTranslation();
  const [selectedTransNo, setSelectedTransNo] = useState(null);
  const loggedInUnitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [searchBtnClicked, setSearchBtnClicked] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openMarkazShareEditModal, setOpenMarkazShareEditModal] = useState(false);
  const [selectedMarkazShareTx, setSelectedMarkazShareTx] = useState(null);
  const [openDeleteRequestModal, setOpenDeleteRequestModal] = useState(false);
  const [selectedDeleteTransaction, setSelectedDeleteTransaction] = useState(null);
  const [deleteModalPosition, setDeleteModalPosition] = useState({ x: 0, y: 0 });
  const [deletionReason, setDeletionReason] = useState("");
  const [deletionError, setDeletionError] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [accountType, setAccountType] = useState("CASH");
  const [transactionData, setTransactionData] = useState();

  const isUrdu = (text) => /[\u0600-\u06FF]/.test(text);

  const getSourceAccountType = useCallback(
    (transaction) => {
      if (!transaction) return "cash";
      if (
        transaction?.accountTypeCode === "cash_in_hand_account" ||
        transaction?.accountTypeCode === "cash" ||
        transaction?.sourceAccountType === "cash"
      ) {
        return "cash";
      }
      if (
        transaction?.accountTypeCode === "amanat_account" ||
        transaction?.accountTypeCode === "amanat" ||
        transaction?.sourceAccountType === "amanat"
      ) {
        return "amanat";
      }
      const personName = (transaction?.personName || "").toLowerCase();
      if (personName.includes("cash in hand") || personName.includes("cash")) {
        return "cash";
      }
      if (personName.includes("amanat")) {
        return "amanat";
      }
      const currentAccountType = transactionData?.searchedAccountType || accountType;
      if (currentAccountType === "AMANAT") {
        return "amanat";
      }
      return "cash";
    },
    [transactionData?.searchedAccountType, accountType]
  );

  const handleDragDeleteModal = (e, ui) => {
    setDeleteModalPosition((prev) => ({
      x: prev.x + ui.deltaX,
      y: prev.y + ui.deltaY,
    }));
  };

  const handleSubmitDeleteRequest = async () => {
    if (!deletionReason.trim()) {
      setDeletionError(t("deletion_reason_required"));
      return;
    }

    setDeleteSubmitting(true);
    setDeletionError("");

    try {
      const sourceAccount = getSourceAccountType(selectedDeleteTransaction);
      const payload = {
        transactionNo: selectedDeleteTransaction.transNo,
        unitId: loggedInUnitId,
        amount: Math.abs(selectedDeleteTransaction.amount),
        memo: deletionReason.trim(),
        date: selectedDeleteTransaction.date || selectedDeleteTransaction.transDate,
        sourceAccountType: sourceAccount,
      };

      const res = await ApiFinance().RequestDeleteMarkazShare(payload);
      if (res?.data?.statusCode === 200 || res?.status === 200) {
        swal({
          title: t("finance_markaz_share_delete_requested"),
          icon: "success",
          buttons: "OK",
        });
        setOpenDeleteRequestModal(false);
        setSelectedDeleteTransaction(null);
        setDeletionReason("");
        handleSearch();
      } else if (res?.data?.statusCode === 300) {
        swal({
          title: t("modification_request_already_pending"),
          icon: "warning",
          buttons: "OK",
        });
      } else {
        swal({
          title: t(res?.data?.message || "error"),
          icon: "error",
          buttons: "OK",
        });
      }
    } catch (err) {
      swal({
        title: t(err?.response?.data?.message || err?.message || "error"),
        icon: "error",
        buttons: "OK",
      });
    } finally {
      setDeleteSubmitting(false);
    }
  };
  const [loading, setLoading] = useState(false);
  const [bankAccountList, setBankAccountList] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const today = new Date();
  const [searchModel, setSearchModel] = useState({
    unitId: loggedInUnitId,
    startDate: new Date(today.getFullYear(), today.getMonth(), 1),
    endDate: new Date(),
    bankAccountId: 0,
  });

  useEffect(() => {
    const financeApi = ApiFinance();

    financeApi
      .GetFinanceBankAccounts(loggedInUnitId)
      .then((res) => {
        const filteredAccounts = (res.data || []).filter(
          (account) =>
            account?.label1 === "cash_in_hand_account" ||
            account?.label1 === "amanat_account",
        );

        if (filteredAccounts.length > 0) {
          setBankAccountList(filteredAccounts);
          setSelectedBankAccount(filteredAccounts[0]);

          // Update model also
          setSearchModel((prev) => ({
            ...prev,
            bankAccountId: filteredAccounts[0].value,
          }));
        } else {
          setBankAccountList([]);
          setSelectedBankAccount(null);

          setSearchModel((prev) => ({
            ...prev,
            bankAccountId: 0,
          }));
        }
      })
      .catch((error) => {
        setBankAccountList([]);
        setSelectedBankAccount(null);
      })
      .finally(() => { });
  }, [loggedInUnitId]);

  const handleSearch = () => {
    if (searchModel.bankAccountId !== 0) {
      setSearchBtnClicked(true);
      setLoading(true);
      ApiFinance()
        .GetCashToAmanatTransactionsByDateAndAccount({
          ...searchModel,
          startDate: convertToDateString(searchModel.startDate),
          endDate: convertToDateString(searchModel.endDate),
        })
        .then((res) => {
          // Handle response data here
          var convertedData = convertDataFromSnakeCaseToCamelCase(res.data);
          setTransactionData({
            ...convertedData,
            searchedBankAccount: selectedBankAccount,
            searchedAccountType: accountType,
          });
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleOnDelete = (value) => {
    swal({
      title: t("common_delete_message"),
      text: t("common_delete_detail_message"),
      className: GetSelectedLanguage() === "en" ? "" : "ur",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then((willDelete) => {
      if (willDelete) {
        setLoading(true);
        ApiFinance()
          .DeleteTransaction(value)
          .then((res) => {
            handleSearch();
            if (res.data.statusCode === "200") {
              swal({
                title: t("transaction_deleted"),
                className: GetSelectedLanguage() === "en" ? "" : "ur",
                icon: "success",
                buttons: "OK",
              });
            } else {
              swal({
                title: t(res.data.message),
                icon: "error",
                buttons: "OK",
              });
            }
            setLoading(false);
          })
          .catch((err) => {
            console.log(err);
            swal({
              title: t(err?.message || "error_occurred"),
              icon: "error",
              buttons: "OK",
            });
            setLoading(false);
          });
      }
    });
  };

  const getAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("fetching_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            res.headers["content-type"] || "application/octet-stream";

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);

          // Open file in new window/tab instead of downloading
          window.open(fileURL, "_blank");

          // Optional: Clean up the object URL after a delay
          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "attachment_fetched", "success");
        } catch (error) {
          console.error("Error opening attachment:", error);
          ShowToast(toastId, "error_opening_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };
  const downloadAttachmentFile = (transNo) => {
    const toastId = toast.loading(t("downloading_attachment"));
    ApiFinance()
      .ViewTransactionAttachment(transNo)
      .then((res) => {
        try {
          const contentType =
            (res.headers &&
              (res.headers["content-type"] || res.headers["Content-Type"])) ||
            "application/octet-stream";
          const disposition =
            (res.headers &&
              (res.headers["content-disposition"] ||
                res.headers["Content-Disposition"])) ||
            "";

          // Try to extract filename from content-disposition header
          let fileName = "attachment";
          const fileNameMatch = disposition.match(
            /filename\*?=(?:UTF-8'')?["']?([^;"']+)/i,
          );
          if (fileNameMatch && fileNameMatch[1]) {
            try {
              fileName = decodeURIComponent(fileNameMatch[1]);
            } catch {
              fileName = fileNameMatch[1];
            }
          }

          // Determine extension from content type if filename has no extension
          const mime = contentType.split(";")[0].trim().toLowerCase();
          const extMap = {
            "application/pdf": "pdf",
            "image/png": "png",
            "image/jpeg": "jpg",
            "image/jpg": "jpg",
            "image/gif": "gif",
          };
          const ext = extMap[mime] || "bin";
          if (!/\.[a-z0-9]+$/i.test(fileName)) {
            fileName = `${fileName}.${ext}`;
          }

          const blob = new Blob([res.data], { type: contentType });
          const fileURL = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = fileURL;
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Cleanup
          setTimeout(() => {
            window.URL.revokeObjectURL(fileURL);
          }, 100);

          ShowToast(toastId, "downloaded_attachment", "success");
        } catch (error) {
          console.error("Error downloading attachment:", error);
          ShowToast(toastId, "error_downloading_attachment", "error");
        }
      })
      .catch((err) => {
        console.error("Error fetching attachment:", err);
        ShowToast(toastId, "error_downloading_attachment", "error");
      });
  };
  const isMarkazShareTransaction = (tx) => {
    if (!tx) return false;
    const personName = tx.personName || "";
    const memo = tx.memo || "";
    return (
      personName.includes("Transit Halqas Share to Markaz") ||
      personName.toLowerCase().includes("markaz share") ||
      memo.toLowerCase().includes("markaz share")
    );
  };

  const formatAccount = useCallback(
    (transaction) => {
      const CashInHand = t("cash_in_hand_account") || "Cash in Hand";
      const AmanatAccount = t("amanat_account") || "Amanat Account";
      const currentAccountType = transactionData?.searchedAccountType || "CASH";

      if (isMarkazShareTransaction(transaction)) {
        const sourceAccount = getSourceAccountType(transaction);
        if (sourceAccount === "amanat" || currentAccountType === "AMANAT") {
          return t("markaz_share_from_amanat") || "Markaz Share from Amanat Account";
        }
        return t("markaz_share_from_cash") || "Markaz Share from Cash in Hand";
      }

      const isNegative = (transaction?.amount || 0) < 0;

      if (currentAccountType === "CASH") {
        // In Cash in Hand: negative means outgoing (Cash -> Amanat), positive means incoming (Amanat -> Cash)
        return isNegative
          ? `${CashInHand} to ${AmanatAccount}`
          : `${AmanatAccount} to ${CashInHand}`;
      } else {
        // In Amanat Account: negative means outgoing (Amanat -> Cash), positive means incoming (Cash -> Amanat)
        return isNegative
          ? `${AmanatAccount} to ${CashInHand}`
          : `${CashInHand} to ${AmanatAccount}`;
      }
    },
    [t, transactionData?.searchedAccountType, getSourceAccountType]
  );

  const resolveAccountType = (account) => {
    if (!account) return "CASH";

    const label =
      typeof account === "string"
        ? account
        : account.label || account.name || account.value || "";

    if (label === "Amanat Account") return "AMANAT";

    if (label === "Cash in Hand" || label === "Cash in Hand Account")
      return "CASH";

    return "CASH"; // safe default
  };

  const { cashToAmanatTotal, amanatToCashTotal, markazShareTotal, hasMarkazShare, cardColMd } = useMemo(() => {
    if (!transactionData?.data) {
      return {
        cashToAmanatTotal: 0,
        amanatToCashTotal: 0,
        markazShareTotal: 0,
        totalTransfers: 0,
        hasMarkazShare: false,
        cardColMd: "4",
      };
    }

    const data = transactionData.data;
    let cashToAmanat = 0;
    let amanatToCash = 0;
    let markazShare = 0;

    if (
      data.cashToAmanatTotal !== undefined &&
      data.cashToAmanatTotal !== null &&
      data.markazShareTotal !== undefined
    ) {
      cashToAmanat = data.cashToAmanatTotal;
      amanatToCash = data.amanatToCashTotal;
      markazShare = data.markazShareTotal;
    } else {
      // Client-side fallback calculation
      const currentAccountType = transactionData?.searchedAccountType || "CASH";
      const transactions = data.transactions || [];

      for (const tx of transactions) {
        const amount = tx.amount || 0;
        const absAmount = Math.abs(amount);

        if (isMarkazShareTransaction(tx)) {
          markazShare += absAmount;
        } else if (currentAccountType === "AMANAT") {
          if (amount < 0) amanatToCash += absAmount;
          else cashToAmanat += absAmount;
        } else {
          if (amount < 0) cashToAmanat += absAmount;
          else amanatToCash += absAmount;
        }
      }
    }

    const total = cashToAmanat + amanatToCash + markazShare;
    const hasMarkaz = markazShare > 0;
    const colMd = hasMarkaz ? "4" : "6";

    return {
      cashToAmanatTotal: cashToAmanat,
      amanatToCashTotal: amanatToCash,
      markazShareTotal: markazShare,
      totalTransfers: total,
      hasMarkazShare: hasMarkaz,
      cardColMd: colMd,
    };
  }, [transactionData]);

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox ">
        <ToastContainer
          autoClose={3000}
          bodyClassName={GetSelectedLanguage()}
          closeOnClick
          pauseOnHover={false}
          draggable
        />
        <div className="ibox-title">
          <h5>{<GetLanguageString props="transactions_manage_transfer" />}</h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            {" "}
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            {<GetLanguageString props="courses_participants_back" />}
          </Button>
        </div>
        <div className="ibox-content">
          <Row>
            {/* Bank Account */}
            <Col md="4">
              <FormGroup>
                <Label className="col-form-label">
                  <GetLanguageString props="fund_bank_account" />
                </Label>
                <Select
                  options={bankAccountList}
                  value={selectedBankAccount}
                  placeholder="Select bank account"
                  className="basic-single no-drag"
                  classNamePrefix="select"
                  onChange={(option) => {
                    setSelectedBankAccount(option);
                    setAccountType(resolveAccountType(option));
                    setSearchModel((prev) => ({
                      ...prev,
                      bankAccountId: option ? option.value : 0,
                    }));
                  }}
                />
              </FormGroup>
            </Col>

            <Col md="8">
              <FormGroup>
                <Col md="12">
                  <DateFilterUI
                    values={searchModel}
                    setValues={setSearchModel}
                    showFullDate={true}
                  />
                </Col>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12" className="text-center">
              <div style={{ marginTop: 10 }}>
                <Button
                  color="primary"
                  className="m-l-sm"
                  onClick={handleSearch}
                >
                  {<GetLanguageString props="common_search" />}
                </Button>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      {searchBtnClicked && (
        <>
          {!loading ? (
            <div className="ibox">
              <div className="ibox-content">
                <>
                  {openEditModal && (
                    <EditCashToAmanatTransaction
                      setModal={setOpenEditModal}
                      transNo={selectedTransNo}
                      onSuccess={handleSearch}
                    />
                  )}
                  {openMarkazShareEditModal && (
                    <EditMarkazShare
                      setModal={setOpenMarkazShareEditModal}
                      transactionData={selectedMarkazShareTx}
                      unitId={loggedInUnitId}
                      onSuccess={handleSearch}
                    />
                  )}
                  {openDeleteRequestModal && selectedDeleteTransaction && (
                    <Draggable
                      position={deleteModalPosition}
                      onDrag={handleDragDeleteModal}
                      handle=".modal-header"
                      cancel=".no-drag"
                    >
                      <Modal
                        isOpen={openDeleteRequestModal}
                        className="inmodal"
                        size="lg"
                        autoFocus={false}
                      >
                        <ModalHeader className="modal-title" tag="h4">
                          <GetLanguageString props="delete_markaz_share_modal_title" />
                        </ModalHeader>
                        <ModalBody className="no-drag">
                          <div className="ibox mb-0">
                            <div className="ibox-content">
                              <div className="alert alert-warning mb-3" style={{ fontSize: "13px" }}>
                                <i className="fa fa-exclamation-triangle mr-2" style={{ marginRight: "6px" }}></i>
                                <GetLanguageString props="delete_markaz_share_confirmation_text" />
                              </div>
                              <Row className="mb-3">
                                <Col md="6">
                                  <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px", marginBottom: "4px" }}>
                                    <GetLanguageString props="funds_account" />
                                  </div>
                                  <div
                                    className="font-weight-bold"
                                    style={{ fontSize: "15px", color: "#2f4050" }}
                                  >
                                    <GetLanguageString
                                      props={
                                        getSourceAccountType(selectedDeleteTransaction) === "cash"
                                          ? "cash_in_hand_account"
                                          : "amanat_account"
                                      }
                                    />
                                  </div>
                                </Col>
                                <Col md="6">
                                  <div style={{ fontSize: "11px", textTransform: "uppercase", color: "#676a6c", fontWeight: "600", letterSpacing: "0.5px", marginBottom: "4px" }}>
                                    <GetLanguageString props="funds_add_fund_amount" />
                                  </div>
                                  <div className="font-weight-bold text-danger" style={{ fontSize: "16px" }}>
                                    Rs {new Intl.NumberFormat().format(Math.abs(selectedDeleteTransaction.amount || 0))}
                                  </div>
                                </Col>
                              </Row>
                              <FormGroup className="mt-3 mb-0">
                                <Label for="deletionReason" className="col-form-label font-weight-bold" style={{ fontSize: "13px" }}>
                                  <GetLanguageString props="deletion_reason" /> <span className="text-danger">*</span>
                                </Label>
                                <Input
                                  id="deletionReason"
                                  type="textarea"
                                  rows="3"
                                  value={deletionReason}
                                  placeholder={t("deletion_reason_placeholder")}
                                  className={`form-control no-drag ${isUrdu(deletionReason) ? "ur" : ""} ${deletionError ? "is-invalid" : ""}`}
                                  onChange={(e) => {
                                    setDeletionReason(e.target.value);
                                    if (deletionError) setDeletionError("");
                                  }}
                                />
                                {deletionError && (
                                  <h4 className="text-error no-drag mt-1">
                                    {deletionError}
                                  </h4>
                                )}
                              </FormGroup>
                            </div>
                          </div>
                        </ModalBody>
                        <ModalFooter className="no-drag">
                          <Button
                            color="white"
                            className={GetCustomStyles().btn_style}
                            onClick={() => setOpenDeleteRequestModal(false)}
                            disabled={deleteSubmitting}
                          >
                            <GetLanguageString props="common_cancel" />
                          </Button>
                          <Button
                            color="danger"
                            className={GetCustomStyles().btn_style}
                            onClick={handleSubmitDeleteRequest}
                            disabled={deleteSubmitting}
                          >
                            {deleteSubmitting && <Spinner size="sm" className="mr-1" />}
                            <GetLanguageString props="request_deletion" />
                          </Button>
                        </ModalFooter>
                      </Modal>
                    </Draggable>
                  )}
                  {transactionData &&
                    transactionData.data.transactions.length === 0 ? (
                    <NoResult />
                  ) : (
                    <>
                      <style>{`
                        .balance-card-hover:hover {
                          transform: translateY(-2px);
                          box-shadow: 0 6px 12px rgba(0,0,0,0.08) !important;
                        }
                      `}</style>
                      <Row className="mb-3 align-items-stretch">
                        {/* Cash to Amanat Card */}
                        <Col xs="12" md={cardColMd} className="mb-2">
                          <div
                            style={{
                              background: "linear-gradient(135deg, #486A84 0%, #39576B 45%, #2D4455 100%)",
                              color: "#ffffff",
                              padding: "8px 12px",
                              borderRadius: "10px",
                              boxShadow: "0 4px 14px rgba(22, 55, 60, 0.12)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              height: "100%",
                              minHeight: "68px",
                              transition: "all 0.25s ease-in-out",
                              cursor: "default",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 20px rgba(22, 55, 60, 0.18)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 4px 14px rgba(22, 55, 60, 0.12)";
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden" }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, flexShrink: 0 }}>
                                <path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4" />
                                <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
                                <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
                              </svg>
                              <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.2px", color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {t("cash_to_amanat_transfers") || "Cash to Amanat Transfers"}
                              </span>
                            </div>

                            <div style={{ fontSize: "18px", fontWeight: "800", letterSpacing: "-0.4px", color: "#ffffff", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                              {loading ? (
                                <BouncingLoader />
                              ) : (
                                `Rs ${new Intl.NumberFormat().format(cashToAmanatTotal)}`
                              )}
                            </div>
                          </div>
                        </Col>

                        {/* Amanat to Cash Card */}
                        <Col xs="12" md={cardColMd} className="mb-2">
                          <div
                            style={{
                              background: "linear-gradient(135deg, #35A08E 0%, #237A6E 45%, #1B6258 100%)",
                              color: "#ffffff",
                              padding: "8px 12px",
                              borderRadius: "10px",
                              boxShadow: "0 4px 14px rgba(22, 55, 60, 0.12)",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "space-between",
                              height: "100%",
                              minHeight: "68px",
                              transition: "all 0.25s ease-in-out",
                              cursor: "default",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 20px rgba(22, 55, 60, 0.18)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 4px 14px rgba(22, 55, 60, 0.12)";
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden" }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, flexShrink: 0 }}>
                                <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                              </svg>
                              <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.2px", color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {t("amanat_to_cash_transfers") || "Amanat to Cash Transfers"}
                              </span>
                            </div>

                            <div style={{ fontSize: "18px", fontWeight: "800", letterSpacing: "-0.4px", color: "#ffffff", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                              {loading ? (
                                <BouncingLoader />
                              ) : (
                                `Rs ${new Intl.NumberFormat().format(amanatToCashTotal)}`
                              )}
                            </div>
                          </div>
                        </Col>

                        {/* Markaz Share Card (shown only when Markaz Share transfers exist) */}
                        {hasMarkazShare && (
                          <Col xs="12" md={cardColMd} className="mb-2">
                            <div
                              style={{
                                background: "linear-gradient(135deg, #D97706 0%, #B45309 45%, #92400E 100%)",
                                color: "#ffffff",
                                padding: "8px 12px",
                                borderRadius: "10px",
                                boxShadow: "0 4px 14px rgba(22, 55, 60, 0.12)",
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                height: "100%",
                                minHeight: "68px",
                                transition: "all 0.25s ease-in-out",
                                cursor: "default",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 8px 20px rgba(22, 55, 60, 0.18)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 14px rgba(22, 55, 60, 0.12)";
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px", whiteSpace: "nowrap", overflow: "hidden" }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.9, flexShrink: 0 }}>
                                  <line x1="22" y1="2" x2="11" y2="13" />
                                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                                </svg>
                                <span style={{ fontSize: "12px", fontWeight: "600", letterSpacing: "0.2px", color: "#ffffff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {t("markaz_share_transfers") || "Markaz Share Transfers"}
                                </span>
                              </div>

                              <div style={{ fontSize: "18px", fontWeight: "800", letterSpacing: "-0.4px", color: "#ffffff", whiteSpace: "nowrap", lineHeight: "1.2" }}>
                                {loading ? (
                                  <BouncingLoader />
                                ) : (
                                  `Rs ${new Intl.NumberFormat().format(markazShareTotal)}`
                                )}
                              </div>
                            </div>
                          </Col>
                        )}
                      </Row>

                      <Table>
                        <thead>
                          <tr style={{ borderBottom: "1px solid black" }}>
                            <th className="text-center">#</th>
                            <th className="text-center">
                              {<GetLanguageString props="funds_date" />}
                            </th>
                            <th className="text-center">
                              {<GetLanguageString props="common_type" />}
                            </th>
                            <th className="text-center">
                              {<GetLanguageString props="transactions_memo" />}
                            </th>

                            <th className="text-center">
                              {
                                <GetLanguageString props="funds_add_fund_amount" />
                              }
                            </th>
                            <th className="text-center">
                              {
                                <GetLanguageString props="transactions_balance" />
                              }
                            </th>

                            <th className="text-center">
                              {<GetLanguageString props="common_action" />}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactionData.data.transactions.map(
                            (transaction, index) => (
                              <tr key={index}>
                                <td className="text-center">{index + 1}</td>
                                <td className="text-center">
                                  {moment(
                                    transaction.date,
                                    "YYYY-MM-DD",
                                  ).format("DD/MM/YYYY")}
                                </td>
                                <td className="text-center">
                                  {formatAccount(transaction)}
                                </td>
                                <td className="text-center">
                                  {transaction.memo || "—"}
                                </td>

                                <td
                                  className={"text-center "}
                                  style={{
                                    color:
                                      transaction.amount < 0 ? "red" : "green",
                                  }}
                                >
                                  <strong>
                                    {new Intl.NumberFormat().format(
                                      transaction.amount,
                                    )}
                                  </strong>
                                </td>

                                <td className="text-center">
                                  {new Intl.NumberFormat().format(
                                    transaction.runningBalance,
                                  )}
                                </td>

                                <td className="text-center">
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      className="btn btn-default"
                                      tag="button"
                                      style={{ padding: "4px 10px" }}
                                    >
                                      <GetLanguageString props="common_action" />
                                      &nbsp;<b className="caret m-l-xs" />
                                    </DropdownToggle>
                                    <DropdownMenu right container="body" style={{ zIndex: 99999 }}>
                                      {isMarkazShareTransaction(transaction) ? (
                                        <>
                                          <DropdownItem
                                            onClick={() => {
                                              setSelectedMarkazShareTx(transaction);
                                              setOpenMarkazShareEditModal(true);
                                            }}
                                          >
                                            <BiSolidEdit color="#3588a2ff" size={18} />
                                            &nbsp;&nbsp;<GetLanguageString props="common_edit" />
                                          </DropdownItem>
                                          <DropdownItem
                                            onClick={() => {
                                              setSelectedDeleteTransaction(transaction);
                                              setDeletionReason("");
                                              setDeletionError("");
                                              setOpenDeleteRequestModal(true);
                                            }}
                                          >
                                            <BsTrash3Fill color="#bd3826ff" size={18} />
                                            &nbsp;&nbsp;<GetLanguageString props="request_deletion" />
                                          </DropdownItem>
                                        </>
                                      ) : (
                                        <>
                                          <DropdownItem
                                            onClick={() =>
                                              getAttachmentFile(transaction.transNo)
                                            }
                                          >
                                            <PiEyeDuotone color="#cf871bff" size={18} />
                                            &nbsp;&nbsp;<GetLanguageString props="view_attachment" />
                                          </DropdownItem>
                                          <DropdownItem
                                            onClick={() =>
                                              downloadAttachmentFile(transaction.transNo)
                                            }
                                          >
                                            <FiDownload color="#35A29F" size={18} />
                                            &nbsp;&nbsp;<GetLanguageString props="download_attachment" />
                                          </DropdownItem>
                                          {transactionData?.searchedAccountType !== "AMANAT" && transaction.amount < 0 && (
                                            <>
                                              <DropdownItem
                                                onClick={() => {
                                                  setSelectedTransNo(transaction.transNo);
                                                  setOpenEditModal(true);
                                                }}
                                              >
                                                <BiSolidEdit color="#3588a2ff" size={18} />
                                                &nbsp;&nbsp;<GetLanguageString props="common_edit" />
                                              </DropdownItem>
                                              <DropdownItem
                                                onClick={() =>
                                                  handleOnDelete(transaction.transNo)
                                                }
                                              >
                                                <BsTrash3Fill color="#bd3826ff" size={18} />
                                                &nbsp;&nbsp;<GetLanguageString props="common_delete" />
                                              </DropdownItem>
                                            </>
                                          )}
                                        </>
                                      )}
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </Table>
                    </>
                  )}
                </>
              </div>
            </div>
          ) : (
            LoadingSpinner()
          )}
        </>
      )}
    </div>
  );
};

export default withRouter(ManageTransfer);
